import _get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { toast } from 'react-toastify';
import { Container, Row, Col, Form, Button, Card, Spinner, Breadcrumb, Collapse, Tabs, Tab, InputGroup, Modal } from 'react-bootstrap';
import { Trash } from 'react-bootstrap-icons';

import { AdminLayout } from 'components/themes/admin';
import { storeOfferingLetterForms } from 'store/actions/master';
import { getOfferingLetterForms, getOfferingLetterForm, addOfferingLetterForm, updateOfferingLetterForm, getJobPosition, getOfferingLetterFormVariables } from 'services/master';
import { isFieldError, getErrorMessage } from 'utils/formHelper';
import { FormInvalidFeedback } from 'components/aimsis/form';
import QuestionBuilder from 'modules/admin/master/QuestionBuilder';

const OfferingLetterFormCreateEdit = () => {
    // State declarations
    const [errMessages, setErrMessages] = useState({});
    const [formLoading, setFormLoading] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [formData, setFormData] = useState(null);
    const [jobPositionsData, setJobPositionsData] = useState([]);

    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const jobPositions = useSelector((state) => state.master.jobPosition);
    const offeringLetterFormsData = useSelector((state) => state.master.offeringLetterForms);

    useEffect(() => {
        if (id) {
            setIsEdit(true);

            // Always fetch from API for edit mode to ensure data is loaded reliably
            const fetchFormData = async () => {
                try {
                    const resp = await getOfferingLetterForm(id);

                    if (resp.success && resp.data) {
                        const formData = _get(resp, 'data.offering_letter_form') || _get(resp, 'data.data') || _get(resp, 'data.form') || resp.data;
                        setFormData(formData);
                    } else {
                        toast.error('Failed to load form data');
                    }
                } catch (error) {
                    toast.error('Failed to load form data');
                }
            };
            fetchFormData();
        }
    }, [id]); // Removed offeringLetterFormsData dependency to always fetch from API

    // Fetch job positions data
    useEffect(() => {
        const fetchJobPositions = async () => {
            try {
                const resp = await getJobPosition();
                if (resp.success && resp.data && resp.data.job_positions) {
                    // API returns job_positions array in data.job_positions
                    const data = Array.isArray(resp.data.job_positions) ? resp.data.job_positions : [];
                    setJobPositionsData(data);
                }
            } catch (error) {
                console.error('Failed to fetch job positions:', error);
            }
        };

        fetchJobPositions();
    }, []);

    const handleSubmit = async (data) => {
        // Transform data structure for API (convert camelCase to snake_case)
        const transformedData = {
            ...data,
            job_positions: data.jobPositions, // convert camelCase to snake_case
            pages: data.pages?.map(page => ({
                ...page,
                questions: page.questions?.map(question => {
                    const transformedQuestion = {
                        ...question,
                        // Convert camelCase to snake_case
                        question_text: question.question_text || question.questionText,
                        question_type: question.question_type || question.questionType,
                        allow_other: question.allowOther,
                        allow_multiple: question.allowMultiple,
                        allowed_extensions: question.allowedExtensions,
                        // Remove camelCase versions
                        questionText: undefined,
                        questionType: undefined,
                        allowOther: undefined,
                        allowMultiple: undefined,
                        allowedExtensions: undefined,
                        // Handle options based on question type
                        options: undefined,
                    };

                    // Handle different question types
                    if (question.type === 'rating') {
                        // For rating: convert options to simple array and include allow_other
                        transformedQuestion.options = question.options?.map(opt => opt.label) || [];
                        transformedQuestion.allow_other = question.allowOther || false;
                    } else if (question.type === 'file') {
                        // For file: include allow_other and allowed_extensions
                        transformedQuestion.allow_other = question.allowOther || false;
                        transformedQuestion.allowed_extensions = question.allowedExtensions || '';
                        transformedQuestion.allow_multiple = question.allowMultiple || false;
                    } else if (['checkbox', 'radiogroup', 'dropdown'].includes(question.type)) {
                        // For checkbox/radiogroup/dropdown: convert options to simple array and include allow_other
                        transformedQuestion.options = question.options?.map(opt => opt.label).filter(label => label.trim() !== '') || [];
                        transformedQuestion.allow_other = question.allowOther || false;
                    }

                    // Handle visibility logic
                    if (question.logic) {
                        if (question.logic.trigger_question_index == null) {
                            transformedQuestion.logic = null;
                        } else {
                            transformedQuestion.logic = {
                                trigger_question_index: question.logic.trigger_question_index,
                                trigger_question_type: question.logic.trigger_question_type,
                                condition_type: question.logic.condition_type,
                                condition_value: question.logic.condition_value,
                                action: question.logic.action
                            };
                        }
                    }

                    return transformedQuestion;
                }) || []
            })) || []
        };

        // Remove camelCase fields from root level
        delete transformedData.jobPositions;

        setErrMessages({});
        setFormLoading(true);

        try {
            let resp;
            if (isEdit) {
                resp = await updateOfferingLetterForm(id, transformedData);
            } else {
                resp = await addOfferingLetterForm(transformedData);
            }

            if (resp.success) {
                // Update Redux store - handle different response structures
                const updatedData = _get(resp, 'data.offering_letter_form') ||
                    _get(resp, 'data.data') ||
                    _get(resp, 'data.form') ||
                    _get(resp, 'data') ||
                    resp.data;

                if (!updatedData) {
                    toast.error('Failed to save form - no data returned');
                    return;
                }

                let newData;

                if (isEdit) {
                    newData = offeringLetterFormsData.map(item =>
                        item.id == id ? updatedData : item
                    );
                } else {
                    newData = [...offeringLetterFormsData, updatedData];
                }

                dispatch(storeOfferingLetterForms(newData));
                toast.success(t('form.saved'));
                navigate('/admin/master/offeringletterforms');
            } else {
                const err = _get(resp, 'data.messages') || {};
                setErrMessages(err);
                toast.error(t('http.request_failed'));
            }
        } catch (error) {
            setFormLoading(false);
            toast.error(t('http.request_failed'));
        }
    };

    const handleCancel = () => {
        navigate('/admin/master/offeringletterforms');
    };

    if (isEdit && !formData) {
        return (
            <Container className='mb-4'>
                <div className='text-center py-5'>
                    <Spinner animation='border' />
                    <p className='mt-2'>{t('loading')}</p>
                </div>
            </Container>
        );
    }

    return (
        <AdminLayout>

            <Container className='mb-4'>
                <Row className='mb-3'>
                    <Col>
                        <Breadcrumb>
                            <Breadcrumb.Item onClick={() => navigate('/admin/master/offeringletterforms')}>
                                {t('menu.offering_letter_forms')}
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>
                                {isEdit ? t('button.update') : t('button.add_new')}
                            </Breadcrumb.Item>
                        </Breadcrumb>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Card>
                            <Card.Header>
                                <h4>{isEdit ? t('form.edit_offering_letter_form') : t('form.create_offering_letter_form')}</h4>
                            </Card.Header>
                            <Card.Body>
                                <OfferingLetterFormBuilder
                                    initialData={formData}
                                    onSubmit={handleSubmit}
                                    onCancel={handleCancel}
                                    formLoading={formLoading}
                                    errMessages={errMessages}
                                    jobPositionsData={jobPositionsData}
                                />
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </AdminLayout>
    );
};

const OfferingLetterFormBuilder = ({
    initialData,
    onSubmit,
    onCancel,
    formLoading,
    errMessages,
    jobPositionsData
}) => {
    const { t } = useTranslation();
    const [variablesData, setVariablesData] = useState({});
    const [showVariableModal, setShowVariableModal] = useState(false);
    const [currentInputPath, setCurrentInputPath] = useState('');

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        watch,
        control,
        reset,
    } = useForm({
        defaultValues: {
            name: '',
            jobPositions: [],
            status: 'active',
            pages: []
        }
    });

    // For form builder - pages and questions
    const { fields: pages, append: appendPage, remove: removePage, replace: replacePages } = useFieldArray({
        control,
        name: 'pages',
    });

    // Set initial values when data is loaded
    useEffect(() => {
        if (initialData) {
            // Prepare the data for form reset
            const formData = {
                ...initialData,
                jobPositions: Array.isArray(initialData.job_positions)
                    ? initialData.job_positions
                        .filter(pos => pos !== null)
                        .map(pos => typeof pos === 'object' ? pos.id : pos)
                        .filter(id => id !== null)
                    : [],
                pages: Array.isArray(initialData.pages)
                    ? initialData.pages.map(page => ({
                        ...page,
                        questions: Array.isArray(page.questions)
                            ? page.questions.map(question => {
                                // Transform options based on question type
                                let transformedOptions = [];

                                if (question.type === 'rating') {
                                    // For rating: convert string array to object array
                                    transformedOptions = Array.isArray(question.options)
                                        ? question.options.map(opt => ({ label: opt, value: opt }))
                                        : [];
                                } else if (['checkbox', 'radiogroup', 'dropdown'].includes(question.type)) {
                                    // For checkbox/radiogroup/dropdown: convert string array to object array
                                    transformedOptions = Array.isArray(question.options)
                                        ? question.options.map(opt => ({ label: opt, value: opt }))
                                        : [];
                                } else if (question.type === 'file') {
                                    // For file: no options needed
                                    transformedOptions = [];
                                } else {
                                    // For other types: keep as is or empty array
                                    transformedOptions = Array.isArray(question.options) ? question.options : [];
                                }

                                return {
                                    ...question,
                                    // Convert snake_case to camelCase
                                    questionText: question.question_text || question.questionText,
                                    questionType: question.question_type || question.questionType,
                                    allowOther: question.allow_other || question.allowOther,
                                    allowMultiple: question.allow_multiple || question.allowMultiple,
                                    allowedExtensions: question.allowed_extensions || question.allowedExtensions,
                                    options: transformedOptions,
                                    // Handle logic field
                                    logic: question.logic ? {
                                        trigger_question_index: question.logic.trigger_question_index,
                                        trigger_question_type: question.logic.trigger_question_type,
                                        condition_type: question.logic.condition_type,
                                        condition_value: question.logic.condition_value,
                                        action: question.logic.action
                                    } : null,
                                    // Remove snake_case versions
                                    question_text: undefined,
                                    question_type: undefined,
                                    allow_other: undefined,
                                    allow_multiple: undefined,
                                    allowed_extensions: undefined,
                                };
                            })
                            : []
                    }))
                    : []
            };

            // Remove snake_case fields from root level
            delete formData.job_positions;

            // Replace the pages array to ensure useFieldArray is synchronized
            if (formData.pages.length > 0) {
                replacePages(formData.pages);
            }

            // Reset the entire form with the prepared data
            reset(formData);
        }
    }, [initialData, reset, replacePages]);

    // Fetch variables data
    useEffect(() => {
        const fetchVariables = async () => {
            try {
                const resp = await getOfferingLetterFormVariables();
                if (resp.success && resp.data && resp.data.variables) {
                    setVariablesData(resp.data.variables);
                }
            } catch (error) {
                console.error('Failed to fetch variables:', error);
            }
        };

        fetchVariables();
    }, []);

    const addPage = () => {
        const newPageIndex = pages.length;
        appendPage({
            title: '',
            order: pages.length + 1,
            questions: [],
        });
        // Set the new page as collapsed by default
        setCollapsedPages(prev => ({
            ...prev,
            [newPageIndex]: true
        }));
    };

    const removePageItem = (index) => {
        removePage(index);
    };

    const truncateText = (text, maxLength = 20) => {
        if (text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    const openVariableModal = (inputPath) => {
        setCurrentInputPath(inputPath);
        setShowVariableModal(true);
    };

    const insertVariable = (variable) => {
        const currentValue = watch(currentInputPath) || '';
        setValue(currentInputPath, currentValue + variable);
        setShowVariableModal(false);
    };

    const onInvalid = () => {
        toast.error('Please fill in all required fields');
    };

    return (
        <Form onSubmit={handleSubmit(onSubmit, onInvalid)}>
            <Row>
                <Col md={6}>
                    <Form.Group className='mb-3'>
                        <Form.Label>{t('form.name')} *</Form.Label>
                        <Form.Control
                            type='text'
                            placeholder={t('form.name')}
                            {...register('name', { required: true })}
                        />
                        <FormInvalidFeedback isInvalid={isFieldError('name', errors, errMessages)}>
                            {getErrorMessage('name', errors, errMessages)}
                        </FormInvalidFeedback>
                    </Form.Group>
                </Col>
            </Row>

            <Row>
                <Col md={6}>
                    <Form.Group className='mb-3'>
                        <Form.Label>{t('form.job_positions')} *</Form.Label>
                        <div className='border rounded p-3' style={{ maxHeight: '200px', overflowY: 'auto' }}>
                            {jobPositionsData.length > 0 ? (
                                jobPositionsData.map((position) => (
                                    <Form.Check
                                        key={position.id}
                                        type='checkbox'
                                        id={`job_position_${position.id}`}
                                        label={position.name}
                                        value={position.id}
                                        checked={(watch('jobPositions') || []).includes(position.id)}
                                        onChange={(e) => {
                                            const currentValues = watch('jobPositions') || [];
                                            if (e.target.checked) {
                                                setValue('jobPositions', [...currentValues, position.id]);
                                            } else {
                                                setValue('jobPositions', currentValues.filter(id => id !== position.id));
                                            }
                                        }}
                                        className='mb-2'
                                    />
                                ))
                            ) : (
                                <div className='text-muted text-center py-3'>
                                    <small>{t('loading_job_positions')}</small>
                                </div>
                            )}
                        </div>
                        <FormInvalidFeedback isInvalid={isFieldError('jobPositions', errors, errMessages)}>
                            {getErrorMessage('jobPositions', errors, errMessages)}
                        </FormInvalidFeedback>
                    </Form.Group>
                </Col>

                <Col md={6}>
                    <Form.Group className='mb-3'>
                        <Form.Label>{t('form.status')} *</Form.Label>
                        <Form.Control as='select' {...register('status', { required: true })}>
                            <option value='active'>{t('active')}</option>
                            <option value='inactive'>{t('inactive')}</option>
                        </Form.Control>
                        <FormInvalidFeedback isInvalid={isFieldError('status', errors, errMessages)}>
                            {getErrorMessage('status', errors, errMessages)}
                        </FormInvalidFeedback>
                    </Form.Group>
                </Col>
            </Row>

            {/* Form Builder Section */}
            <Card className='mt-4'>
                <Card.Header className='d-flex justify-content-between align-items-center'>
                    <h5 className='mb-0'>{t('form.form_builder')}</h5>
                    <div className='d-flex gap-2'>
                        <Button type='button' variant='primary' size='sm' onClick={addPage}>
                            {t('form.add_page')}
                        </Button>
                    </div>
                </Card.Header>
                <Card.Body>
                    {pages.length === 0 ? (
                        <div className='text-center py-5 text-muted'>
                            <p>{t('form.no_pages_added_yet')}</p>
                            <Button type='button' variant='outline-primary' onClick={addPage}>
                                {t('form.add_first_page')}
                            </Button>
                        </div>
                    ) : (
                        <Tabs 
                            defaultActiveKey="page-0" 
                            id="offering-letter-form-tabs" 
                            className="mb-3 flex-nowrap" 
                            style={{ overflowX: 'auto' }}
                        >
                            {pages.map((page, pageIndex) => (
                                <Tab 
                                    key={page.id} 
                                    eventKey={`page-${pageIndex}`} 
                                    title={
                                        <div className="d-flex align-items-center justify-content-between" style={{ minWidth: '120px' }}>
                                            <span className="text-truncate">{t('form.page')} {pageIndex + 1}: {truncateText(page.title || t('form.untitled_page'), 10)}</span>
                                            <Button
                                                type='button'
                                                variant='link'
                                                size='sm'
                                                className="p-0 ms-1 text-danger"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removePageItem(pageIndex);
                                                }}
                                                title={t('form.remove_page')}
                                            >
                                                <Trash size={14} />
                                            </Button>
                                        </div>
                                    }
                                >
                                    <div className="mt-3">
                                        <Row className='mb-3'>
                                            <Col md={8}>
                                                <Form.Group>
                                                    <Form.Label>{t('form.page_title')}</Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            type='text'
                                                            placeholder={t('form.page_title')}
                                                            {...register(`pages.${pageIndex}.title`)}
                                                        />
                                                        <Button
                                                            variant='outline-secondary'
                                                            onClick={() => openVariableModal(`pages.${pageIndex}.title`)}
                                                            title={t('form.insert_variable')}
                                                        >
                                                            {t('form.variables')}
                                                        </Button>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                        </Row>

                                        {/* Questions section */}
                                        <QuestionBuilder
                                            pageIndex={pageIndex}
                                            control={control}
                                            register={register}
                                            setValue={setValue}
                                            errors={errors}
                                            errMessages={errMessages}
                                            watch={watch}
                                            openVariableModal={openVariableModal}
                                        />
                                    </div>
                                </Tab>
                            ))}
                        </Tabs>
                    )}
                </Card.Body>
            </Card>

            <div className='d-flex justify-content-end gap-2 mt-4'>
                <Button variant='secondary' onClick={onCancel} disabled={formLoading}>
                    {t('button.cancel')}
                </Button>
                <Button type='submit' variant='primary' disabled={formLoading}>
                    {formLoading ? <Spinner animation='border' variant='light' size='sm' className='me-2' /> : null}
                    {t('button.save')}
                </Button>
            </div>

            <Modal show={showVariableModal} onHide={() => setShowVariableModal(false)} size='lg'>
                <Modal.Header closeButton>
                    <Modal.Title>{t('form.variable_bank')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('form.select_variable_to_insert')}</p>
                    <div className='row'>
                        {Object.entries(variablesData).map(([variable, description]) => (
                            <div key={variable} className='col-md-6 mb-2'>
                                <Button
                                    variant='outline-primary'
                                    className='w-100 text-start'
                                    onClick={() => insertVariable(variable)}
                                >
                                    <strong>{variable}</strong><br />
                                    <small className='text-muted'>{description}</small>
                                </Button>
                            </div>
                        ))}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant='secondary' onClick={() => setShowVariableModal(false)}>
                        {t('button.close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Form>
    );
};

export default OfferingLetterFormCreateEdit;